import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/* Custom Code */
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { historyTableProps } from "@store/Themes/common/props/history";
/* Custom Code */

/* Store Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { orderStatusStyles } from "@store/Themes/common/mixins/orderStatus";
/* Store Custom Code - End */

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface OrderSummaryCardStyles {
    orderSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    webOrderNumberGridItem?: GridItemProps;
    webOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    // Custom Code
    orderNumberCell?: DataTableCellProps;
    dateCell?: DataTableCellProps;
    shipToCell?: DataTableCellProps;
    customerPOCell?: DataTableCellProps;
    statusCell?: DataTableCellProps;
    totalCell?: DataTableCellProps;
    // Store Custom Code
    statusIsOrdered?: InjectableCss;
    statusIsPicked?: InjectableCss;
    statusIsSent?: InjectableCss;
    statusIsInvoiced?: InjectableCss;
    statusIsPaid?: InjectableCss;
    statusIsCanceled?: InjectableCss;
    statusIsFailed?: InjectableCss;
}

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    orderNumberCell: historyTableProps.orderNumberCell,
    dateCell: historyTableProps.dateCell,
    shipToCell: historyTableProps.shipToCell,
    customerPOCell: historyTableProps.customerPOCell,
    statusCell: historyTableProps.statusCell,
    totalCell: historyTableProps.totalCell,
    // Store Custom Code
    statusIsOrdered: {
        css: orderStatusStyles("info", "info.light"),
    },
    statusIsPicked: {
        css: orderStatusStyles("success", "success.light"),
    },
    statusIsSent: {
        css: orderStatusStyles("success", "success.light"),
    },
    statusIsInvoiced: {
        css: orderStatusStyles("danger", "danger.light"),
    },
    statusIsPaid: {
        css: css`
            display: inline-block;
        `,
    },
    statusIsCanceled: {
        css: orderStatusStyles("warning", "warning.light"),
    },
    statusIsFailed: {
        css: orderStatusStyles("danger", "danger.light"),
    },
};

const OrderSummaryCard: FC<Props> = ({ language, order, orderSettings, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(orderSummaryCardStyles, extendedStyles));

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: order.orderDate,
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });

    /* Store Custom Code - Start */
    const getStatusWithStyle = (statusCode?: string, statusText?: string) => {
        let boxStyle;
        switch (statusCode) {
            case "1": {
                boxStyle = styles.statusIsOrdered;
                break;
            }
            case "2": {
                boxStyle = styles.statusIsPicked;
                break;
            }
            case "3": {
                boxStyle = styles.statusIsSent;
                break;
            }
            case "4": {
                boxStyle = styles.statusIsPaid;
                break;
            }
            case "5": {
                boxStyle = styles.statusIsOrdered;
                break;
            }
            case "9": {
                boxStyle = styles.statusIsCanceled;
                break;
            }
            case "100": {
                boxStyle = styles.statusIsFailed;
                break;
            }
        }
        return <StyledWrapper {...boxStyle}>{statusText}</StyledWrapper>;
    };
    /* Store Custom Code - End */

    /* Custom Code */
    const orderNumber = orderSettings.showWebOrderNumber
        ? order.webOrderNumber || order.erpOrderNumber
        : order.erpOrderNumber || order.webOrderNumber;

    const shipTo = `${order.stCompanyName} ${order.stAddress1} ${order.stAddress2} ${order.shipToCity} ${order.shipToState}`;

    return (
        <DataTableRow key={order.id}>
            <DataTableCell {...styles.orderNumberCell} data-test-selector={`orderSummaryCard_${orderNumber}`}>
                {orderNumber && <OrderDetailPageTypeLink title={orderNumber} orderNumber={orderNumber} />}
            </DataTableCell>
            <DataTableCell {...styles.dateCell} data-test-selector="orderSummaryCard_Date">
                {orderDateDisplay}
            </DataTableCell>
            <DataTableCell {...styles.shipToCell}>{shipTo}</DataTableCell>
            <DataTableCell {...styles.customerPOCell}>{order.customerPO}</DataTableCell>
            {/* Store Custom Code - Start */}
            <DataTableCell {...styles.statusCell}>
                {getStatusWithStyle(order.status, order.statusDisplay)}
            </DataTableCell>
            {/* Store Custom Code - End */}
            <DataTableCell {...styles.totalCell}>{order.orderGrandTotalDisplay}</DataTableCell>
        </DataTableRow>
    );
    /* Custom Code */
};

export default connect(mapStateToProps)(OrderSummaryCard);
